import type { Character, PlayerCharacter, SkillName, AbilityName } from "../types/entity.js";
import { DEATH_SAVE_NONE } from "../types/entity.js";
import { loadItem } from "../loaders/ItemLoader.js";
import { addItem, setEquipment, emptyInventory } from "../inventory/CharacterInventory.js";
import type { EquippableItem } from "../types/items.js";
import { InventoryStore } from "../../store/InventoryStore.js";
import { mintUuid } from "./partyIdentity.js";
import { pregensUrl } from "../adventure/ActiveAdventure.js";

/**
 * EV-E8 — adventure-authored pregen parties.
 *
 * An adventure may ship `data/party/pregens.json`; when present, the
 * launch picker offers it in place of the engine's hardcoded Blueberries
 * tile. Blueprints are self-contained (explicit maxHp/ac/castingAbility)
 * so custom classes work without class records — though shipping the
 * records too keeps level-up and chargen coherent.
 */

export interface PregenBlueprint {
  readonly name: string;
  readonly characterClass: string;
  readonly race?: string;
  readonly background?: string;
  readonly abilities: Character["abilities"];
  readonly maxHp: number;
  readonly ac: number;
  readonly castingAbility?: AbilityName;
  readonly spellSlots?: Record<number, number>;
  readonly skillProficiencies?: readonly SkillName[];
  readonly saveProficiencies?: readonly AbilityName[];
  readonly portrait?: string;
  readonly startingEquipment?: readonly string[];
  readonly knownSpells?: readonly string[];
}

export interface PregenRoster {
  readonly title: string;
  readonly blurb: string;
  readonly members: readonly PregenBlueprint[];
}

/** Validate a parsed pregens.json. Returns null on shape errors. */
export function parsePregenRoster(raw: unknown): PregenRoster | null {
  if (typeof raw !== "object" || raw === null) return null;
  const rec = raw as { title?: unknown; blurb?: unknown; members?: unknown };
  if (typeof rec.title !== "string" || !Array.isArray(rec.members) || rec.members.length === 0)
    return null;
  for (const m of rec.members) {
    if (typeof m !== "object" || m === null) return null;
    const b = m as Record<string, unknown>;
    if (
      typeof b.name !== "string" ||
      typeof b.characterClass !== "string" ||
      typeof b.maxHp !== "number" ||
      typeof b.ac !== "number" ||
      typeof b.abilities !== "object"
    )
      return null;
  }
  return {
    title: rec.title,
    blurb: typeof rec.blurb === "string" ? rec.blurb : "",
    members: rec.members as PregenBlueprint[],
  };
}

/** Fetch the active adventure's pregen roster; null when none ships. */
export async function loadPregenRoster(
  url: string = pregensUrl(),
  fetchImpl: typeof fetch = fetch,
): Promise<PregenRoster | null> {
  try {
    const res = await fetchImpl(url);
    if (!res.ok) return null;
    return parsePregenRoster(await res.json());
  } catch {
    return null;
  }
}

export function makePregenParty(roster: PregenRoster): Character[] {
  return roster.members.map(fromBlueprint);
}

/** Load + auto-equip each blueprint's starting gear (Blueberries pattern). */
export async function equipPregens(
  roster: PregenRoster,
  members: readonly PlayerCharacter[],
): Promise<void> {
  for (const bp of roster.members) {
    const member = members.find((m) => m.name === bp.name);
    if (!member) continue;

    let inv = emptyInventory();
    for (const itemId of bp.startingEquipment ?? []) {
      const item = await loadItem(itemId);
      if (!item) continue;
      const result = addItem(inv, item, 1, member.abilities.STR);
      inv = result.inventory;
      if (item.slot && result.added > 0) {
        inv = setEquipment(inv, item.slot, item as EquippableItem);
      }
    }
    InventoryStore.set(member.instanceId, inv);
  }
}

function fromBlueprint(bp: PregenBlueprint): Character {
  return {
    kind: "player",
    instanceId: `pc-${mintUuid()}`,
    dataId: bp.characterClass,
    name: bp.name,
    characterClass: bp.characterClass,
    race: bp.race ?? "human",
    background: bp.background ?? "adventurer",
    portrait: bp.portrait ?? null,
    level: 1,
    position: { x: 0, z: 0 },
    hp: bp.maxHp,
    maxHp: bp.maxHp,
    armorClass: bp.ac,
    speed: { walk: 30 },
    abilities: { ...bp.abilities },
    proficiencyBonus: 2,
    spellSlots: { ...(bp.spellSlots ?? {}) },
    ...(bp.castingAbility !== undefined ? { castingAbility: bp.castingAbility } : {}),
    xp: 0,
    knownSpells: [...(bp.knownSpells ?? [])],
    reactions: [],
    skillProficiencies: new Set(bp.skillProficiencies ?? []),
    saveProficiencies: new Set(bp.saveProficiencies ?? []),
    expertiseSkills: new Set(),
    conditions: new Set(),
    deathSaves: DEATH_SAVE_NONE,
    formationSlot: null,
  };
}
